import { useCallback } from 'react';
import { IconArrowDown, IconArrowUp, IconMinus } from '@tabler/icons-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../utils/bnFormat';

// Small pieces shared by the Contest* components of the /contest page.
// NB: the global `p { font-weight: 400 !important }` strips bold from <p> — bold text uses span/div.

/** Digits as the user reads them: Bangla digits in Bangla, as-is in English. */
export function useDigits() {
    const { currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    return useCallback((value) => (isBn ? toBnDigits(value) : String(value ?? '')), [isBn]);
}

/** Number formatter: grouped (1,25,000) with Bangla digits in Bangla. */
export function useNumFmt() {
    const digits = useDigits();
    return useCallback((n) => digits(Number(n ?? 0).toLocaleString('en-IN')), [digits]);
}

// Pastel initial-circle tints (used when a participant has no profile photo).
const TINTS = [
    'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
    'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
    'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
];
const tintOf = (name) => {
    let h = 0;
    for (const ch of name || '') h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return TINTS[h % TINTS.length];
};
const firstLetter = (name) => (Array.from((name || '').trim())[0] || '?').toUpperCase();

/** "Thana, District" of a leaderboard entry. */
export const locationOf = (entry) => [entry?.thana, entry?.district].filter(Boolean).join(', ');

/** Profile photo, or the first letter of the name on a soft tint. Size / ring come from `className`. */
export function ContestAvatar({ src, name, className = 'h-9 w-9 text-sm', tint }) {
    return (
        <div className={`${className} ${tint || tintOf(name)} flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full font-bold`}>
            {src ? <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" style={{ maxWidth: 'none' }} /> : firstLetter(name)}
        </div>
    );
}

/** ↑ / ↓ rank movement since the previous hourly snapshot (move > 0 = moved up). */
export function RankMove({ move, fmt }) {
    if (!move) return <span className="inline-flex items-center text-zinc-300 dark:text-zinc-600"><IconMinus size={12} /></span>;
    const up = move > 0;
    return (
        <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-px text-[10px] font-semibold ${up ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400' : 'bg-red-50 text-red-500 dark:bg-red-900/20 dark:text-red-400'}`}>
            {up ? <IconArrowUp size={10} /> : <IconArrowDown size={10} />}{fmt(Math.abs(move))}
        </span>
    );
}

// The card surface every block of the page shares.
export const CARD = 'rounded-[20px] border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900';

// The status chip at the right of a bonus-task row (waiting / added / rejected / quiz done today) …
export const CHIP = 'inline-flex h-[38px] min-w-[104px] items-center justify-center gap-1.5 rounded-[10px] px-3 text-[13px] font-semibold';
// … and the small pill after a task's title saying how often it can be done (blue = every day, grey = once per contest).
export const BADGE = 'inline-flex items-center rounded-full px-2 py-px text-xs font-semibold';
export const BADGE_DAILY = `${BADGE} bg-doc-blue-50 text-doc-blue-700 dark:bg-doc-blue-700/20 dark:text-doc-blue-300`;
export const BADGE_ONCE = `${BADGE} bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300`;
